/**
 * COA suggestion helpers.
 *
 * `metadata.coa_inferred` is written by the COA writer when the extracted
 * value differs from the existing field. Keys prefixed with `_` are
 * bookkeeping (source_doc, confidence) and never shown as suggestions.
 *
 * This module is the single source of truth for:
 *  - the allow-list (mirrors `apply_coa_suggestion` SQL)
 *  - human labels
 *  - grouping paired fields (price, UoM) into a single user-facing entry
 */

export const COA_SUGGESTION_ALLOWED_FIELDS = [
  'strain_name','species','product_form','grower_email','grower_name','company_name',
  'currency','region','harvest_date',
  'quantity_value','quantity_unit','price_value','price_unit',
  'dimension','unit_weight_g','units_per_pack','packs_per_case',
] as const;

export type COASuggestionField = (typeof COA_SUGGESTION_ALLOWED_FIELDS)[number];

const FIELD_LABEL: Record<COASuggestionField, string> = {
  strain_name: 'Strain name',
  species: 'Species',
  product_form: 'Product form',
  grower_email: 'Contact email',
  grower_name: 'Contact person',
  company_name: 'Company name',
  currency: 'Currency',
  region: 'Region',
  harvest_date: 'Harvest / Production Date',
  quantity_value: 'Quantity',
  quantity_unit: 'Quantity unit',
  price_value: 'Price',
  price_unit: 'Price unit',
  dimension: 'Dimension',
  unit_weight_g: 'Unit weight (g)',
  units_per_pack: 'Units per pack',
  packs_per_case: 'Packs per case',
};

export interface COASuggestion {
  /** Stable key for React; derived from the field group. */
  key: string;
  /** Human label for the chip header. */
  label: string;
  /** Server fields the apply RPC will receive — sent together as one batch. */
  fields: COASuggestionField[];
  /** Suggested values keyed by field. */
  suggestedValues: Partial<Record<COASuggestionField, unknown>>;
  /** Current listing values keyed by field (for display). */
  currentValues: Partial<Record<COASuggestionField, unknown>>;
}

/** Field groups: paired fields rendered + applied together. */
const FIELD_GROUPS: Array<{ key: string; label: string; fields: COASuggestionField[] }> = [
  { key: 'price',    label: 'Price',    fields: ['price_value', 'price_unit'] },
  { key: 'quantity', label: 'Quantity', fields: ['quantity_value', 'quantity_unit'] },
];

function isInferredKeyDisplayable(k: string): boolean {
  if (k.startsWith('_')) return false;
  return (COA_SUGGESTION_ALLOWED_FIELDS as readonly string[]).includes(k);
}

/**
 * Derive ordered suggestion entries from a `coa_inferred` blob plus the
 * current listing values. Returns an empty array if there's nothing to show.
 */
export function deriveCOASuggestions(
  coaInferred: Record<string, unknown> | null | undefined,
  current: Partial<Record<COASuggestionField, unknown>>,
): COASuggestion[] {
  if (!coaInferred) return [];
  const present = new Set(
    Object.keys(coaInferred).filter(isInferredKeyDisplayable),
  ) as Set<COASuggestionField>;
  if (present.size === 0) return [];

  const out: COASuggestion[] = [];
  const consumed = new Set<COASuggestionField>();

  for (const group of FIELD_GROUPS) {
    const groupHit = group.fields.filter(f => present.has(f));
    if (groupHit.length === 0) continue;
    // Always send the full group together so partial-apply can't happen.
    const fields = group.fields;
    const suggestedValues: COASuggestion['suggestedValues'] = {};
    const currentValues: COASuggestion['currentValues'] = {};
    for (const f of fields) {
      suggestedValues[f] = coaInferred[f];
      currentValues[f] = current[f];
      consumed.add(f);
    }
    out.push({ key: group.key, label: group.label, fields, suggestedValues, currentValues });
  }

  for (const f of COA_SUGGESTION_ALLOWED_FIELDS) {
    if (consumed.has(f)) continue;
    if (!present.has(f)) continue;
    out.push({
      key: f,
      label: FIELD_LABEL[f],
      fields: [f],
      suggestedValues: { [f]: coaInferred[f] },
      currentValues: { [f]: current[f] },
    });
  }

  return out;
}

/** Stable count for banner copy. */
export function countCOASuggestions(
  coaInferred: Record<string, unknown> | null | undefined,
): number {
  return deriveCOASuggestions(coaInferred, {}).length;
}

/** Pretty-print a suggestion value for the chip. */
export function formatSuggestionValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}